/**
 * 流式翻译状态管理钩子
 *
 * 提供流式翻译的状态管理功能，包括流式文本状态和流式状态标志
 *
 * 核心功能:
 * 1. 根据翻译状态管理流式文本
 * 2. 跟踪流式翻译是否正在进行
 * 3. 提供流式文本和状态用于UI渲染
 *
 * @example
 * ```tsx
 * const { streamText, isStreaming } = useStreamingTranslation(translationState);
 *
 * return (
 *   <StreamView
 *     text={streamText}
 *     isStreaming={isStreaming}
 *   />
 * );
 * ```
 */
import { useState, useEffect } from 'react';
/**
 * 流式翻译状态管理钩子
 * 基于翻译状态管理流式翻译的UI状态
 *
 * @param translationState 翻译状态对象(简化版)
 * @returns 流式翻译状态和方法
 */
export function useStreamingTranslation(translationState) {
    // 状态管理
    const [streamText, setStreamText] = useState('');
    const [isStreaming, setIsStreaming] = useState(false);
    // 监听翻译状态变化
    useEffect(() => {
        if (translationState.isLoading) {
            // 当外部指示正在加载（即流式进行中）
            if (!isStreaming) {
                // 只有在内部状态不是 streaming 时才设置为 true
                setIsStreaming(true);
            }
            // 只有当外部的流式文本与内部的不同时才更新
            if (translationState.streamingText !== streamText) {
                setStreamText(translationState.streamingText || ''); // 使用 || '' 以防 undefined/null
            }
            else {
                /* empty */
            }
        }
        else {
            // 当外部指示加载结束（即流式已停止）
            if (isStreaming || streamText !== '') {
                // 只有在需要改变状态时才调用 setState
                setIsStreaming(false);
                setStreamText('');
            }
            else {
                /* empty */
            }
        }
        // 依赖项保持不变，React会比较每个依赖项的引用/值
    }, [translationState.isLoading, translationState.streamingText, streamText, isStreaming]);
    /**
     * 重置流式状态
     * 将流式文本和状态重置为初始值
     */
    const resetStreamingState = () => {
        setStreamText('');
        setIsStreaming(false);
    };
    // 返回状态和方法
    return {
        streamText,
        isStreaming,
        setStreamText,
        setIsStreaming,
        resetStreamingState,
    };
}
export default useStreamingTranslation;
//# sourceMappingURL=useStreamingTranslation.js.map